import * as React from 'react';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { Text } from '@/hen-ui/atomics/Text';
import { cn } from '@/hen-ui/utils';
import {
  circularProgressCenterLabelNumberVariant,
  circularProgressCenterLabelPercentVariant,
  circularProgressCenterLabelVariants,
  circularProgressDimensions,
  circularProgressIndicatorVariants,
  circularProgressRootVariants,
  circularProgressTrackVariants,
  type CircularProgressIndicatorVariantProps,
  type CircularProgressRootVariantProps,
} from './circular-progress.variants';

/** Progressbar role/range are computed — consumers must not override via DOM props. */
type CircularProgressOmittedHtmlKeys =
  | 'children'
  | 'color'
  | 'role'
  | 'aria-valuenow'
  | 'aria-valuemin'
  | 'aria-valuemax'
  | 'aria-valuetext';

export interface CircularProgressOwnProps
  extends Omit<
      React.HTMLAttributes<HTMLElement>,
      CircularProgressOmittedHtmlKeys
    >,
    CircularProgressRootVariantProps,
    CircularProgressIndicatorVariantProps {
  /** Current value on the `[-max, max]` scale; arc fill is `|clamp(value/max)|` of a full turn. */
  value: number;
  /**
   * Scale maximum; value is interpreted as a fraction of max (default 100).
   * Non-finite or `<= 0` values are treated as `100` so the gauge and ARIA range stay valid.
   */
  max?: number;
  /**
   * Center label override. When omitted, defaults to the **arc fill percentage**
   * `clamp(value / max, -1, 1) * 100` (e.g. value 50 / max 200 → `25%`), matching the ring.
   */
  label?: string;
  /**
   * Overrides default `aria-valuetext` (arc ratio as a spoken percent). Use when `label` is
   * non-numeric or decorative but assistive tech should still announce a specific value string.
   */
  announcedValueText?: string;
}

/**
 * `size` defaults to `'md'` (48×48 ring per EDUN-11528) when omitted.
 * With `asChild: true`, `children` must be exactly one React element (the host).
 */
export type CircularProgressProps = CircularProgressOwnProps &
  (
    | {
        asChild?: false;
        children?: React.ReactNode;
      }
    | {
        asChild: true;
        children: React.ReactElement;
      }
  );

function normalizeMax(max: number): number {
  if (!Number.isFinite(max) || max <= 0) {
    return 100;
  }
  return max;
}

/** Maps non-finite inputs to the closest progressbar values so arc math and `aria-valuenow` stay aligned. */
function sanitizeValue(value: number, safeMax: number): number {
  if (Number.isNaN(value)) {
    return 0;
  }
  if (value === Number.POSITIVE_INFINITY) {
    return safeMax;
  }
  if (value === Number.NEGATIVE_INFINITY) {
    return -safeMax;
  }
  return value;
}

function clampRatio(value: number, max: number): number {
  const r = value / max;
  if (Number.isNaN(r) || !Number.isFinite(r)) {
    return 0;
  }
  if (r > 1) {
    return 1;
  }
  if (r < -1) {
    return -1;
  }
  return r;
}

/** Maps IEEE `-0` to `0` so default labels and `aria-valuetext` never surface signed negative zero. */
function normalizeSignedZero(value: number): number {
  return Object.is(value, -0) ? 0 : value;
}

/** Normalizes `-0` / `-0.0…` percent cores from overrides while keeping values like `-0.5`. */
function normalizePercentLabelCore(core: string): string {
  if (core === '-0' || /^-0\.0+$/.test(core)) {
    return '0';
  }
  return core;
}

function formatPercentLabel(value: number): string {
  const v = normalizeSignedZero(value);
  const rounded = normalizeSignedZero(v % 1 === 0 ? v : Number(v.toFixed(1)));
  return `${rounded}%`;
}

/** Spoken percent for `aria-valuetext`, aligned with `ratio * 100` (same rounding as default label). */
function formatAriaPercentSpeech(percent: number): string {
  const v = normalizeSignedZero(percent);
  const spoken = normalizeSignedZero(v % 1 === 0 ? v : Number(v.toFixed(1)));
  return `${spoken} percent`;
}

/** When label ends with `%`, render digits and sign separately from the symbol (different type tokens). */
function splitPercentLabel(
  text: string,
): { core: string; suffix: string } | null {
  if (text.endsWith('%')) {
    const core = normalizePercentLabelCore(text.slice(0, -1));
    return { core, suffix: '%' };
  }
  return null;
}

export const CircularProgress = React.forwardRef<
  HTMLElement,
  CircularProgressProps
>(
  (
    {
      className,
      asChild = false,
      children,
      value,
      max: maxProp = 100,
      size = 'md',
      variant = 'primary',
      label: labelProp,
      announcedValueText,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      ...props
    },
    ref,
  ) => {
    const safeMax = normalizeMax(maxProp);
    const safeValue = sanitizeValue(value, safeMax);
    const resolvedSize = size ?? 'md';
    const { px: dimension, stroke: strokeWidth } =
      circularProgressDimensions[resolvedSize];
    const numberVariant =
      circularProgressCenterLabelNumberVariant[resolvedSize];
    const percentVariant =
      circularProgressCenterLabelPercentVariant[resolvedSize];
    const padding = 1;
    const radius = Math.max(
      dimension / 2 - strokeWidth / 2 - padding,
      strokeWidth,
    );
    const circumference = 2 * Math.PI * radius;
    const ratio = clampRatio(safeValue, safeMax);
    const magnitude = Math.abs(ratio);
    const dashLength = circumference * magnitude;

    const ariaValueNow = normalizeSignedZero(
      Math.min(safeMax, Math.max(-safeMax, safeValue)),
    );
    const displayPercent = normalizeSignedZero(ratio * 100);
    const labelText = labelProp ?? formatPercentLabel(displayPercent);
    const labelParts = splitPercentLabel(labelText);
    const ariaValueText =
      announcedValueText ?? formatAriaPercentSpeech(displayPercent);

    const cx = dimension / 2;
    const cy = dimension / 2;
    const ariaValueMin = 0 - safeMax;
    const ariaValueMax = safeMax;

    const a11yLabel =
      ariaLabel !== undefined
        ? { 'aria-label': ariaLabel }
        : ariaLabelledBy !== undefined
          ? { 'aria-labelledby': ariaLabelledBy }
          : { 'aria-label': 'Circular progress' as const };

    const rootClassName = cn(
      circularProgressRootVariants({ size: resolvedSize }),
      className,
    );

    const progressbarProps = {
      role: 'progressbar' as const,
      'aria-valuemin': ariaValueMin,
      'aria-valuemax': ariaValueMax,
      'aria-valuenow': ariaValueNow,
      'aria-valuetext': ariaValueText,
      ...a11yLabel,
    };

    const rootProps = {
      className: rootClassName,
      ...props,
      ...progressbarProps,
    };

    const gauge = (
      <>
        <svg
          width='100%'
          height='100%'
          viewBox={`0 0 ${dimension} ${dimension}`}
          className='col-start-1 row-start-1 block size-full shrink-0 overflow-visible'
          aria-hidden
          focusable={false}
        >
          <g transform={`rotate(-90 ${cx} ${cy})`}>
            <circle
              className={cn(circularProgressTrackVariants({ variant }))}
              cx={cx}
              cy={cy}
              r={radius}
              strokeWidth={strokeWidth}
            />
            {magnitude > 0 && (
              <g
                transform={
                  ratio < 0
                    ? `translate(${cx} ${cy}) scale(1,-1) translate(${-cx} ${-cy})`
                    : undefined
                }
              >
                <circle
                  className={cn(circularProgressIndicatorVariants({ variant }))}
                  cx={cx}
                  cy={cy}
                  r={radius}
                  strokeWidth={strokeWidth}
                  strokeLinecap='round'
                  strokeDasharray={`${dashLength} ${circumference}`}
                />
              </g>
            )}
          </g>
        </svg>
        <div
          aria-hidden
          className={cn(
            circularProgressCenterLabelVariants({ size: resolvedSize }),
          )}
        >
          {labelParts ? (
            <>
              <Text
                tag='span'
                variant={numberVariant}
                color='heading'
                weight='bold'
                className='inline'
              >
                {labelParts.core}
              </Text>
              <Text
                tag='span'
                variant={percentVariant}
                color='heading'
                weight='bold'
                className='inline'
              >
                {labelParts.suffix}
              </Text>
            </>
          ) : (
            <Text
              tag='span'
              variant={numberVariant}
              color='heading'
              weight='bold'
              align='center'
              className='inline'
            >
              {labelText}
            </Text>
          )}
        </div>
      </>
    );

    if (asChild) {
      if (
        React.Children.count(children) !== 1 ||
        !React.isValidElement(children) ||
        children.type === React.Fragment
      ) {
        throw new Error(
          'CircularProgress: asChild requires exactly one React element child (not Fragment; e.g. <section>...</section>).',
        );
      }
      return (
        <Slot ref={ref} {...rootProps}>
          {gauge}
          <Slottable>{children}</Slottable>
        </Slot>
      );
    }

    return (
      <div ref={ref as React.Ref<HTMLDivElement>} {...rootProps}>
        {gauge}
        {children}
      </div>
    );
  },
);

CircularProgress.displayName = 'CircularProgress';
